import React, { useState } from 'react';
import { IConfig } from '../../Interfaces/IConfig';
import { CustomCircularProgress } from '../generic/CustomCircularProgress';
import { LoginMain } from './LoginMain';
import { LoginF2A } from './LoginF2A';

export type LoginAuthResponse = {
  detail?: string;
  TempSessionToken?: string;
  idContact?: number;
  SessionToken?: string;
  TimestampSessionToken?: string;
  Role?: string;
  Scope?: string;
  passwordResetRequired?: string;
  DeviceToken?: string;
};

export type SendAuthFn = (
  sendObject: unknown,
  callAfterSave: (res: LoginAuthResponse) => void,
  errorFunction: () => void
) => Promise<void>;

interface IProps {
  configObject: IConfig;
  msgParent: string | null;
  enableAutoLogin: boolean;
}

// Direkteinstieg in den 2FA-Schritt nach dem Passwort-Reset: Die Reset-Seite
// legt idContact + TempSessionToken in der sessionStorage ab und leitet auf
// "/" weiter. Hier wird das einmalig ausgelesen, sodass die Login-Maske
// (Page 0) übersprungen und direkt der 2FA-Code abgefragt wird.
const PW_RESET_2FA_KEY = 'pwReset2FA';

const readPwReset2FA = (): { idContact: number; token: string } | null => {
  try {
    const raw = sessionStorage.getItem(PW_RESET_2FA_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.idContact === 'number' && typeof parsed?.token === 'string') {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return null;
};

export const LoginDriver: React.FC<IProps> = ({
  msgParent,
  configObject,
  enableAutoLogin,
}) => {
  const pwReset = readPwReset2FA();

  const [currentPage, setCurrentPage] = useState(pwReset ? 1 : 0);
  const [msg, setMsg] = useState(msgParent === null ? '' : msgParent);
  const [softLockTime, setSoftLockTime] = useState<null | string>(
    localStorage.getItem('soft_lock_till')
  );

  const [isLoading, setIsLoading] = useState(false);
  const [tmpSessionToken, setTmpSessionToken] = useState<string>(
    pwReset?.token ?? ''
  );
  const [idContact, setIdContact] = useState<number>(pwReset?.idContact ?? -1);

  // Handoff nur einmal verwenden — bei einem manuellen Reload mitten im
  // 2FA-Schritt fällt man dann sauber auf die normale Login-Maske zurück.
  React.useEffect(() => {
    if (pwReset) {
      sessionStorage.removeItem(PW_RESET_2FA_KEY);
    }
  }, []);

  const sendPost: SendAuthFn = async (
    sendObject,
    callAfterSave,
    errorFunction
  ) => {
    setIsLoading(true);

    await fetch('/api/oauth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sendObject),
    })
      .then(res => {
        if (res.status === 200 || res.status === 409) {
          setIsLoading(false);
          return res.json();
        } else {
          throw Error(String(res.status));
        }
      })
      .then(res => {
        setMsg('');
        setIsLoading(false);
        callAfterSave(res);
      })
      .catch(error => {
        if (String(error).includes('401')) {
          setMsg('Die Anmeldedaten scheinen nicht korrekt zu sein');
        } else if (String(error).includes('403')) {
          setMsg(`Ihr Konto wurde deaktiviert!`);
        } else {
          setMsg(
            `Es ist ein unbekannter Fehler aufgetreten. Möglicherweise ist der Dienst vorübergehend offline.`
          );
        }
        setIsLoading(false);
        errorFunction();
      });
  }

  if (isLoading) {
    return <CustomCircularProgress />;
  } else if (currentPage === 0) {
    return (
      <LoginMain
        configObject={configObject}
        msg={msg}
        setIdContact={setIdContact}
        setTmpSessionToken={setTmpSessionToken}
        setCurrentPage={setCurrentPage}
        sendFunction={sendPost}
        enableAutoLogin={enableAutoLogin}
        setSoftLockTime={setSoftLockTime}
        softLockTime={softLockTime}
      />
    );
  } else if (currentPage === 1) {
    return (
      <LoginF2A
        configObject={configObject}
        msg={msg}
        idContact={idContact}
        tmpSessionToken={tmpSessionToken}
        sendFunction={sendPost}
        setSoftLockTime={setSoftLockTime}
        softLockTime={softLockTime}
      />
    );
  } else {
    // Hinweis: Sobald LoginF2A erfolgreich ist und passwordResetRequired === 'true'
    // gesetzt wurde, übernimmt der SecurityDriver und rendert <LoginData/>.
    return <>FEHLER!</>;
  }
};
